import { useEffect, useState } from 'react'
import { mediaUrl } from '../../services/config'

// Telegram-style @-mention autocomplete. Hosting input owns the textarea
// state; this component is a controlled overlay that:
//   1. Watches `text` + `caret` for an active `@` token before the caret
//   2. Filters `candidates` by fuzzy prefix match on the token tail
//   3. Lets the user navigate with ↑/↓/Tab/Enter/Esc via the imperative
//      keyboard handler exposed via `onKeyDown`
//   4. Inserts the chosen mention into the source string via `onPick`
//
// We deliberately avoid wrapping the textarea so the existing layout +
// emoji overlay + send button keep working unchanged. The picker itself
// is `position: absolute` above the input and uses pointer events so it
// doesn't steal focus.

export interface MentionCandidate {
  id: string
  /** First+last (preferred) or username — already-resolved display name. */
  displayName: string
  /** Always present, used for matching when the displayName isn't typed. */
  username: string
  avatarUrl?: string | null
}

interface ActiveToken {
  /** Index in source string where the `@` lives (the @ itself). */
  start: number
  /** Index in source string just past the last char of the tail. */
  end: number
  /** Lowercased tail without the leading @. */
  query: string
}

/** Detect the active @-token immediately preceding the caret. Returns
 *  null if the caret isn't inside a token (no @ found, whitespace
 *  between, etc). The token can match `@`, `@d`, `@dmi tr` (no — space
 *  ends the token), `@all`, etc. We support up to 32 chars in the tail
 *  to avoid runaway matches. */
export function detectMentionToken(text: string, caret: number): ActiveToken | null {
  if (caret <= 0) return null
  // Walk backwards looking for `@`. Bail on whitespace or after 32 chars.
  let i = caret - 1
  let chars = 0
  while (i >= 0 && chars <= 32) {
    const c = text[i]
    if (c === '@') {
      // The `@` must be at the start OR preceded by whitespace / punctuation
      // — otherwise an email like `foo@bar` would trigger the picker.
      const prev = i > 0 ? text[i - 1] : ''
      if (prev === '' || /[\s(\[{,;]/.test(prev)) {
        return {
          start: i,
          end: caret,
          query: text.slice(i + 1, caret).toLowerCase(),
        }
      }
      return null
    }
    if (/\s/.test(c)) return null
    i--
    chars++
  }
  return null
}

interface Props {
  candidates: MentionCandidate[]
  /** Current source value (textarea.value). */
  text: string
  /** Caret position (textarea.selectionStart). */
  caret: number
  /** Anchor element used to position the popover (textarea or its
   *  wrapper). The popover sits above-left of this rect. */
  anchorEl: HTMLElement | null
  /** Called when the user picks an entry. The host should splice the
   *  mention into its source string + advance caret past the inserted
   *  range. The inserted form is `<@userId> ` (trailing space). */
  onPick: (entry: MentionCandidate, token: ActiveToken) => void
}

export interface MentionPickerHandle {
  /** Returns true if the picker swallowed the key (caller should
   *  suppress its own handler). */
  handleKeyDown: (e: React.KeyboardEvent) => boolean
  /** True iff the picker is currently visible. */
  isOpen: boolean
}

export default function MentionPicker({ candidates, text, caret, anchorEl, onPick }: Props) {
  const token = detectMentionToken(text, caret)
  const [highlighted, setHighlighted] = useState(0)
  // Reset cursor whenever the active query string changes.
  useEffect(() => {
    setHighlighted(0)
  }, [token?.query])

  const filtered = (() => {
    if (!token) return [] as MentionCandidate[]
    const q = token.query
    if (!q) return candidates.slice(0, 8)
    const matches = candidates.filter((c) => {
      const a = c.displayName.toLowerCase()
      const b = c.username.toLowerCase()
      return a.includes(q) || b.includes(q)
    })
    return matches.slice(0, 8)
  })()

  // Keyboard handler exposed on a global ref so the host textarea's
  // onKeyDown can delegate. We stash it on `window.__mentionPicker` to
  // avoid lifting it via React refs (the host owns the textarea).
  useEffect(() => {
    const handler = {
      isOpen: !!token && filtered.length > 0,
      handleKeyDown: (e: React.KeyboardEvent) => {
        if (!token || filtered.length === 0) return false
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setHighlighted((h) => (h + 1) % filtered.length)
          return true
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          setHighlighted((h) => (h - 1 + filtered.length) % filtered.length)
          return true
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault()
          onPick(filtered[highlighted], token)
          return true
        }
        if (e.key === 'Escape') {
          e.preventDefault()
          // Insert a literal space so the token gets dismissed without
          // being re-detected on the next keystroke.
          onPick({ id: '', displayName: '', username: '' } as any, token)
          return true
        }
        return false
      },
    }
    ;(window as any).__mentionPicker = handler
    return () => {
      if ((window as any).__mentionPicker === handler) {
        delete (window as any).__mentionPicker
      }
    }
  }, [token, filtered, highlighted, onPick])

  if (!token || filtered.length === 0 || !anchorEl) return null

  const rect = anchorEl.getBoundingClientRect()
  const top = rect.top - 8 // popover bottom-aligns to anchor top
  const left = rect.left

  return (
    <div
      className="fixed z-50 bg-card border border-border rounded-lg shadow-xl py-1 min-w-[260px] max-h-[260px] overflow-y-auto"
      style={{
        bottom: window.innerHeight - top,
        left,
      }}
      // Don't steal focus from the textarea on click — handled by mousedown
      // so the textarea keeps focus and the splice keeps caret position.
      onMouseDown={(e) => e.preventDefault()}
    >
      {filtered.map((c, i) => (
        <button
          key={c.id}
          type="button"
          onClick={() => onPick(c, token)}
          onMouseEnter={() => setHighlighted(i)}
          className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
            i === highlighted ? 'bg-accent text-foreground' : 'text-foreground hover:bg-accent'
          }`}
        >
          {c.avatarUrl ? (
            <img src={mediaUrl(c.avatarUrl)} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
          ) : (
            <span className="w-6 h-6 rounded-full bg-discord-blurple text-white text-xs flex items-center justify-center shrink-0 font-semibold">
              {(c.displayName[0] || c.username[0] || '?').toUpperCase()}
            </span>
          )}
          <span className="flex-1 min-w-0 truncate">
            <span className="font-medium">{c.displayName}</span>
            {c.username !== c.displayName && (
              <span className="ml-1.5 text-muted-foreground">@{c.username}</span>
            )}
          </span>
        </button>
      ))}
    </div>
  )
}

/** Helper used by the host's onPick: returns the new (text, caret) pair
 *  after splicing `@<displayName> ` (or a bare space for the dismiss
 *  case) in place of the active token. We deliberately insert the
 *  visible display name rather than `<@userId>` so the textarea's caret
 *  position matches what the user sees — the chip-rendered overlay can
 *  be much narrower than a UUID, and the offset between caret and chip
 *  produced an obvious "big space" gap. The host translates these
 *  visible mentions to `<@userId>` at submit time via
 *  `transformMentionsForSend`. */
export function applyMention(
  text: string,
  token: ActiveToken,
  entry: MentionCandidate | null
): { text: string; caret: number } {
  if (!entry || !entry.id) {
    // Dismiss path — leave the @-tail as-is, just append a space so the
    // detector stops matching.
    const next = `${text.slice(0, token.end)} ${text.slice(token.end)}`
    return { text: next, caret: token.end + 1 }
  }
  const insert = `@${entry.displayName} `
  const next = `${text.slice(0, token.start)}${insert}${text.slice(token.end)}`
  return { text: next, caret: token.start + insert.length }
}

/** Walk a typed message body and replace every `@<displayName>` sequence
 *  registered in `refs` with the canonical `<@userId>` token form the
 *  rest of the system understands. Longest names first so a "Konstantin
 *  Fedchenko" entry doesn't get half-eaten by a stray "Konstantin"
 *  entry. Names are looked up case-sensitively against the original
 *  insertion (the picker controls the display string, so the user can't
 *  accidentally retype with a different case and still match). */
export function transformMentionsForSend(
  text: string,
  refs: Map<string, string>
): string {
  if (!text || refs.size === 0) return text
  const names = Array.from(refs.keys()).sort((a, b) => b.length - a.length)
  let out = text
  for (const name of names) {
    const userId = refs.get(name)
    if (!userId) continue
    // Escape the display name for use in a regex.
    const safe = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    out = out.replace(new RegExp(`@${safe}\\b`, 'g'), `<@${userId}>`)
  }
  return out
}
